/**
 * Pieces the Broker Data Pipeline pages share. The generic ones (stats, window picker, the fetch lifecycle) are the Load
 * Data Pipeline's, imported from there; only what is the broker pipeline's own lives here.
 */

import Alert from '@mui/joy/Alert'
import Typography from '@mui/joy/Typography'
import type { BrokerPipelineBudget, BrokerPipelineBudgetUse, BrokerPipelineWriter, BrokerSearchStatement, BrokerSearchStatus } from '@numeo/types-core'

/** The writer's sidebar name and route: `FMCSA` → `W-FMCSA`, `/broker-pipeline/writer/fmcsa`. */
export const writerTitle = (writer: string): string => `W-${writer}`
export const writerPath = (writer: string): string => `/broker-pipeline/writer/${writer.toLowerCase()}`

/** A writer's budget in words: "3,000 a day", "70,000 a week". */
export const budgetLabel = (budget: BrokerPipelineBudget): string =>
    `${budget.updates.toLocaleString()} ${budget.days === 1 ? 'a day' : budget.days === 7 ? 'a week' : `every ${budget.days} days`}`

/**
 * The share of its budget a writer published over the budget's own period (the server reads it, whatever window the page
 * shows), or null when there is nothing to compare. A weekly writer is never measured against a day's share of its week.
 */
export const budgetShare = (budget: BrokerPipelineBudget | null, use: BrokerPipelineBudgetUse | null): number | null =>
    budget && use && budget.updates > 0 ? use.published / budget.updates : null

/** The period a budget is read over, as the pages say it. */
export const budgetPeriod = (budget: BrokerPipelineBudget): string => (budget.days === 1 ? 'the last 24 hours' : `the last ${budget.days} days`)

/** "3 min ago", "2 h ago": how long a writer has been quiet. */
export const quietFor = (lastEventAt: string | null | undefined): string => {
    if (!lastEventAt) return 'nothing in this window'
    const minutes = Math.max(0, Math.round((Date.now() - Date.parse(lastEventAt)) / 60_000))
    return minutes < 90 ? `${minutes} min ago` : `${Math.round(minutes / 60)} h ago`
}

/** What a page shows for a writer with no code: it has published nothing and keeps no collections. */
export function NotYetBuilt({ declaration }: { declaration: BrokerPipelineWriter }) {
    return (
        <Alert color="neutral" variant="soft" sx={{ mb: 3 }}>
            <Typography level="body-sm">
                <strong>{writerTitle(declaration.writer)} is not yet built.</strong>{' '}
                {declaration.adr ? `${declaration.adr} declares it` : 'No ADR declares it yet'}
                {declaration.budget !== null ? `, with a budget of ${budgetLabel(declaration.budget)} updates` : ''}. Its activity and notes show here once its
                code publishes.
            </Typography>
        </Alert>
    )
}

/** A time in the viewer's own zone, or a dash. */
export const when = (iso: string | undefined): string => (iso ? new Date(iso).toLocaleString() : '—')

/** Who stated a value: its update type and source. */
export const said = (by: BrokerSearchStatement): string => `${by.type} · ${by.source}`

/** A brokerage's status as a chip colour. */
export const statusColor = (status: BrokerSearchStatus): 'success' | 'danger' | 'warning' =>
    status === 'VERIFIED' ? 'success' : status === 'SUPPRESSED' ? 'danger' : 'warning'

/** One brokerage's page in the explorer. */
export const brokeragePath = (mc: string): string => `/broker-pipeline/consumer/brokerage/${encodeURIComponent(mc)}`
